import { Formik } from 'formik';
import type { FormikHelpers } from 'formik';
import { Form } from '@gusto/workbench-formik';
import { Alert, Box, Flex } from '@gusto/workbench';
import { useSearchParams } from 'react-router-dom';
import hrosSvg from 'explore/HROS.svg';
import { leadFormInitialValues } from 'components/company_leads/lead_form_v2/constants';
import { getAnswersFromStorage } from 'packages/signup/helpers/storageHelpers';
import { useSignupContext } from 'packages/signup/components/signupProvider';
import { SurveyQuestionsKeys } from 'packages/signup/components/SurveyQuestions';
import trackFormSubmitClickedWithPii from 'packages/signup/analytics/trackFormSubmitClickedWithPii';
import UnifiedLearnMoreJourneyQuestions from 'packages/signup/components/explore/UnifiedLearnMoreJourneyQuestions';
import type { CompanyLead } from 'packages/signup/components/signupForm/types';
import type { ExploreItemValues } from 'packages/signup/components/explore/types';
import { WRAP_TEXT_PLAIN } from 'packages/signup/components/explore/constants';
import { removePasswordOfCompanyLeadObject } from 'packages/signup/helpers/validateSurvey';
import EmbeddedLeadForm, {
  onSubmitEmbeddedLeadForm,
} from 'packages/signup/components/explore/embeddedLeadForm/EmbeddedLeadForm';
import type { EmbeddedLeadFormExploreValues } from 'packages/signup/components/explore/embeddedLeadForm/types';
import track from 'packages/signup/components/explore/track';
import {
  embeddedLeadFormValidationSchema,
  embeddedLeadFormHiddenValidationSchema,
} from 'packages/signup/components/explore/embeddedLeadForm/embedded_lead_form_validation_schema';
import useCompanyLead from 'packages/signup/components/signupForm/use_company_lead';
import ProgressBar from 'components/progress_bar';
import { calculateExploreProgressPercentage } from 'packages/signup/components/explore/exploreProgressBarHelper';
import {
  LeadOriginSubtypeValue,
  SubmittedFromValue,
} from 'packages/signup/components/explore/embeddedLeadForm/types';
import { localStorageGetItem } from 'entrypoints/storage_helpers';
import useIsFilledLeadForm from 'packages/signup/components/explore/embeddedLeadForm/useIsFilledLeadForm';

export default function Explore() {
  const signupContext = useSignupContext();
  const { whoToPay, numberStates, prevPayrollProvider, whatToUse, whatToUseFindYourPlan } =
    signupContext;
  const { loading: companyLeadLoading, companyLead } = useCompanyLead();
  const [searchParams] = useSearchParams();
  const isFilledLeadForm = useIsFilledLeadForm();
  const objectPlainText = WRAP_TEXT_PLAIN.LEARNING_JOURNEY_TEXT_DEFAULT;

  const onClickedFormikData = (values: EmbeddedLeadFormExploreValues) => {
    const companyLeadObj: CompanyLead = {};
    companyLeadObj.first_name = values.firstName;
    companyLeadObj.last_name = values.lastName;
    companyLeadObj.company_name = values.companyName;
    companyLeadObj.email = values.email;
    companyLeadObj.phone = values.phone;
    companyLeadObj.number_employees = Number(values.numberEmployees);
    companyLeadObj.submitted_from = SubmittedFromValue.LEARNING_JOURNEY_FORM;
    companyLeadObj.lead_origin_subtype = LeadOriginSubtypeValue.LEARNING_JOURNEY_FORM;
    companyLeadObj.lead_origin = 'GCorp';

    const companyLeadObjectWithoutPassword = removePasswordOfCompanyLeadObject(companyLeadObj);
    trackFormSubmitClickedWithPii(companyLeadObjectWithoutPassword);
  };

  const onSubmit = async (
    values: EmbeddedLeadFormExploreValues,
    formikOpts: FormikHelpers<any>
  ) => {
    const pages = getAnswersFromStorage(SurveyQuestionsKeys.WHAT_TO_USE);
    const nextStep = '/invite/company/how-it-works';

    await onSubmitEmbeddedLeadForm(
      values,
      formikOpts,
      LeadOriginSubtypeValue.LEARNING_JOURNEY_FORM,
      isFilledLeadForm
    );
    track('explore', 'ClickLetsGo', pages);

    globalThis.location.href = searchParams ? `${nextStep}?${searchParams}` : nextStep;
  };

  const getInitialValues = () => {
    let exploreInitialValues: any;

    if (!whatToUse || !whatToUseFindYourPlan) {
      exploreInitialValues = [];
    } else {
      exploreInitialValues = [...whatToUse];
    }

    return {
      whoToPay,
      numberStates,
      prevPayrollProvider,
      whatToUse,
      whatToUseFindYourPlan,
      explore: exploreInitialValues as ExploreItemValues[],
    };
  };

  const getValidationSchema = () => {
    return localStorageGetItem('filledLeadForm')
      ? embeddedLeadFormHiddenValidationSchema()
      : embeddedLeadFormValidationSchema();
  };

  const loading = companyLeadLoading;

  if (loading) return null;

  return (
    <>
      <div>
        <Flex width="100wh" flexDirection="column" flexDirectionMd="row">
          <ProgressBar
            percentage={calculateExploreProgressPercentage(signupContext)}
            headerId="#lead-nav"
          />
          <Flex
            heightMd="100vh"
            widthLg="50%"
            widthMd="54%"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            className="get-started-box bg-parsnip-200"
          >
            <Box className="col-12 col-md-9 flex-basis-10" textAlign="center">
              <h2>{objectPlainText.headerCopy}</h2>
              <p className="explore-subcopy">{objectPlainText.subheaderCopy}</p>
            </Box>
            <img className="hros" alt={objectPlainText.imageAltText} src={hrosSvg} />
          </Flex>
          <Flex
            widthLg="50%"
            widthMd="46%"
            alignItems="center"
            justifyContent="center"
            className="question-box"
            marginTopMd="80px"
            flexDirection="column"
          >
            <Formik
              initialValues={
                localStorageGetItem('filledLeadForm')
                  ? getInitialValues()
                  : { ...getInitialValues(), ...leadFormInitialValues(companyLead) }
              }
              validationSchema={getValidationSchema}
              onSubmit={onSubmit}
            >
              {({ errors, submitCount, values }) => {
                return (
                  <Form>
                    <UnifiedLearnMoreJourneyQuestions />
                    <EmbeddedLeadForm
                      primaryCTACopy={objectPlainText.primaryCTA}
                      primaryCTAOnClick={() => onClickedFormikData(values)}
                    />

                    {submitCount > 0 && Object.keys(errors).length > 0 && (
                      <Alert
                        style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
                        id="survey-validation-alert"
                        label=""
                        severity="error"
                      >
                        Please fill out or select all the required fields above.
                      </Alert>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </Flex>
          <img className="hros-mobile" alt={objectPlainText.imageAltText} src={hrosSvg} />
        </Flex>
      </div>
    </>
  );
}
